import { ColorVariant } from "@/constants/theme";
import { createThemeColorObject } from "@/utils/theme/createThemeColorObject";
import { createThemeSizeObject } from "@/utils/theme/createThemeSizeObject";
import type { DeepPartial } from "@/types";
import type { Theme } from "@/types/theme";

const theme: DeepPartial<Theme> = {
  setup: {
    font: {
      customFonts: [],
    },
    coreTheme: "B2B",
  },
  colorSetup: {
    brand: {
      primary: "var(--informa-color-setup-brand-primary, #000000)",
      secondary: "var(--informa-color-setup-brand-secondary, #000000)",
      accent: "var(--informa-color-setup-brand-accent, #000000)",
      contrast: "var(--informa-color-setup-brand-contrast)",
    },
    standard: {
      main: "var(--informa-color-setup-standard-main, #000000)",
      dark: "var(--informa-color-setup-standard-dark, #000000)",
      light: "var(--informa-color-setup-standard-light, #FFFFFF)",
      contrast: "var(--informa-color-setup-standard-contrast)",
    },
    alert: {
      success: "var(--informa-color-setup-alert-success, #00FF00)",
      warning: "var(--informa-color-setup-alert-warning, #FFFF00)",
      error: "var(--informa-color-setup-alert-error, #FF0000)",
      info: "var(--informa-color-setup-alert-info, #0000FF)",
    },
    custom: {
      color1: "var(--informa-color-setup-custom-color-1)",
      color2: "var(--informa-color-setup-custom-color-2)",
      color3: "var(--informa-color-setup-custom-color-3)",
      color4: "var(--informa-color-setup-custom-color-4)",
    },
  },
  fontSettings: {
    bodyXSmall: {
      fontSize: {
        mobile: createThemeSizeObject("0.625", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.1"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    bodySmall: {
      fontSize: {
        mobile: createThemeSizeObject("0.875", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.5"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    bodyRegular: {
      fontSize: {
        mobile: createThemeSizeObject("1", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.625"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    bodyLarge: {
      fontSize: {
        mobile: createThemeSizeObject("1.125", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.75"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    bodyXLarge: {
      fontSize: {
        mobile: createThemeSizeObject("1.375", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.75"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    bodyXXLarge: {
      fontSize: {
        mobile: createThemeSizeObject("1.5", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.75"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    headerH1: {
      fontSize: {
        mobile: createThemeSizeObject("3", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.25"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    headerH2: {
      fontSize: {
        mobile: createThemeSizeObject("2.75", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.25"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    headerH3: {
      fontSize: {
        mobile: createThemeSizeObject("2.25", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.25"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    headerH4: {
      fontSize: {
        mobile: createThemeSizeObject("1.75", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.25"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    headerH5: {
      fontSize: {
        mobile: createThemeSizeObject("1.375", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.25"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    headerH6: {
      fontSize: {
        mobile: createThemeSizeObject("1.25", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.25"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    menu: {
      fontSize: {
        mobile: createThemeSizeObject("1.375", "rem"),
        desktop: createThemeSizeObject("1.125", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("500"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    buttonSmall: {
      fontSize: {
        mobile: createThemeSizeObject("1", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.5", "rem"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    buttonMedium: {
      fontSize: {
        mobile: createThemeSizeObject("1.125", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.5", "rem"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
    buttonLarge: {
      fontSize: {
        mobile: createThemeSizeObject("1.375", "rem"),
      },
      fontWeight: {
        mobile: createThemeSizeObject("400"),
      },
      lineHeight: {
        mobile: createThemeSizeObject("1.5", "rem"),
      },
      letterSpacing: {
        mobile: createThemeSizeObject("0", "px"),
      },
      textTransform: {
        mobile: "none",
      },
    },
  },
  buttonSettings: {
    sizes: {
      small: {
        paddingTop: {
          mobile: createThemeSizeObject("0.5", "rem"),
        },
        paddingBottom: {
          mobile: createThemeSizeObject("0.5", "rem"),
        },
        paddingLeft: {
          mobile: createThemeSizeObject("2", "rem"),
        },
        paddingRight: {
          mobile: createThemeSizeObject("2", "rem"),
        },
        borderTopWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderBottomWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderLeftWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderRightWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderTopLeftRadius: {
          mobile: createThemeSizeObject("2.75", "rem"),
        },
        borderTopRightRadius: {
          mobile: createThemeSizeObject("2.75", "rem"),
        },
        borderBottomLeftRadius: {
          mobile: createThemeSizeObject("2.75", "rem"),
        },
        borderBottomRightRadius: {
          mobile: createThemeSizeObject("2.75", "rem"),
        },
      },
      medium: {
        paddingTop: {
          mobile: createThemeSizeObject("0.625", "rem"),
        },
        paddingBottom: {
          mobile: createThemeSizeObject("0.625", "rem"),
        },
        paddingLeft: {
          mobile: createThemeSizeObject("2", "rem"),
        },
        paddingRight: {
          mobile: createThemeSizeObject("2", "rem"),
        },
        borderTopWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderBottomWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderLeftWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderRightWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderTopLeftRadius: {
          mobile: createThemeSizeObject("3", "rem"),
        },
        borderTopRightRadius: {
          mobile: createThemeSizeObject("3", "rem"),
        },
        borderBottomLeftRadius: {
          mobile: createThemeSizeObject("3", "rem"),
        },
        borderBottomRightRadius: {
          mobile: createThemeSizeObject("3", "rem"),
        },
      },
      large: {
        paddingTop: {
          mobile: createThemeSizeObject("0.875", "rem"),
        },
        paddingBottom: {
          mobile: createThemeSizeObject("0.875", "rem"),
        },
        paddingLeft: {
          mobile: createThemeSizeObject("2", "rem"),
        },
        paddingRight: {
          mobile: createThemeSizeObject("2", "rem"),
        },
        borderTopWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderBottomWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderLeftWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderRightWidth: {
          mobile: createThemeSizeObject("2", "px"),
        },
        borderTopLeftRadius: {
          mobile: createThemeSizeObject("3.5", "rem"),
        },
        borderTopRightRadius: {
          mobile: createThemeSizeObject("3.5", "rem"),
        },
        borderBottomLeftRadius: {
          mobile: createThemeSizeObject("3.5", "rem"),
        },
        borderBottomRightRadius: {
          mobile: createThemeSizeObject("3.5", "rem"),
        },
      },
    },
    styles: {
      primary: {
        color: {
          normal: createThemeColorObject(ColorVariant.White),
          hover: createThemeColorObject(ColorVariant.White),
        },
        background: {
          normal: createThemeColorObject(ColorVariant.Accent),
          hover: createThemeColorObject(ColorVariant.Accent),
        },
        borderColor: {
          normal: createThemeColorObject(ColorVariant.Accent),
          hover: createThemeColorObject(ColorVariant.Accent),
        },
      },
      secondary: {
        color: {
          normal: createThemeColorObject(ColorVariant.White),
          hover: createThemeColorObject(ColorVariant.White),
        },
        background: {
          normal: createThemeColorObject(ColorVariant.Secondary),
          hover: createThemeColorObject(ColorVariant.Secondary),
        },
        borderColor: {
          normal: createThemeColorObject(ColorVariant.Secondary),
          hover: createThemeColorObject(ColorVariant.Secondary),
        },
      },
      tertiary: {
        color: {
          normal: createThemeColorObject(ColorVariant.White),
          hover: createThemeColorObject(ColorVariant.White),
        },
        background: {
          normal: createThemeColorObject(ColorVariant.Primary),
          hover: createThemeColorObject(ColorVariant.Primary),
        },
        borderColor: {
          normal: createThemeColorObject(ColorVariant.Primary),
          hover: createThemeColorObject(ColorVariant.Primary),
        },
      },
      quaternary: {
        color: {
          normal: createThemeColorObject(ColorVariant.White),
          hover: createThemeColorObject(ColorVariant.White),
        },
        background: {
          normal: createThemeColorObject(ColorVariant.Dark),
          hover: createThemeColorObject(ColorVariant.Dark),
        },
        borderColor: {
          normal: createThemeColorObject(ColorVariant.Dark),
          hover: createThemeColorObject(ColorVariant.Dark),
        },
      },
    },
  },
  menuSettings: {
    height: {
      mobile: createThemeSizeObject("3.5", "rem"),
      desktop: createThemeSizeObject("4.5", "rem"),
    },
    gutter: {
      mobile: createThemeSizeObject("0"),
      desktop: createThemeSizeObject("2", "rem"),
    },
    textColor: createThemeColorObject(ColorVariant.White),
    background: createThemeColorObject(ColorVariant.Dark),
  },
  siteBuilder: {
    baseModule: {
      paddingTop: {
        mobile: createThemeSizeObject("2.5", "rem"),
        desktop: createThemeSizeObject("5", "rem"),
      },
      paddingBottom: {
        mobile: createThemeSizeObject("2.5", "rem"),
        desktop: createThemeSizeObject("5", "rem"),
      },
    },
  },
};

export default theme as Theme;
